import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useClienteAuth } from "@/contexts/ClienteAuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, FileText, Upload, Trash2, Loader2, Wrench, ClipboardCheck, Download, HardDrive, X } from "lucide-react";
import { toast } from "sonner";

const TIPO_DOC_LABEL = {
  mantencion_preventiva: "Mantención preventiva",
  mantencion_correctiva: "Mantención correctiva",
  documento_normativo: "Documento normativo",
  factura_compra: "Factura de compra",
  manual_equipo: "Manual del equipo",
  certificado: "Certificado",
  otro: "Otro",
};

export default function ClienteEquipo() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api } = useClienteAuth();
  const [eq, setEq] = useState(null);
  const [upl, setUpl] = useState(null); // {file, tipo_documento, descripcion, fecha_documento}
  const [busy, setBusy] = useState(false);
  const [solTipo, setSolTipo] = useState(null); // "preventiva"|"correctiva"|null
  const [solComentario, setSolComentario] = useState("");
  const fileRef = useRef(null);

  const load = () => api.get(`/api/portal/equipos/${id}`).then((r) => setEq(r.data))
    .catch(() => { toast.error("Equipo no encontrado"); navigate("/cliente/dashboard"); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const pickFile = (f) => {
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) return toast.error("Máximo 10MB por documento");
    setUpl({ file: f, tipo_documento: "", descripcion: "", fecha_documento: "" });
  };

  const subir = async () => {
    if (!upl.tipo_documento) return toast.error("Indica qué tipo de documento es");
    setBusy(true);
    const t = toast.loading("Subiendo y comprimiendo…");
    try {
      const fd = new FormData();
      fd.append("file", upl.file);
      fd.append("tipo_documento", upl.tipo_documento);
      fd.append("descripcion", upl.descripcion);
      fd.append("fecha_documento", upl.fecha_documento);
      await api.post(`/api/portal/equipos/${id}/documentos`, fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      toast.dismiss(t); toast.success("Documento guardado");
      setUpl(null); load();
    } catch (e) { toast.dismiss(t); toast.error(e.response?.data?.detail || "Error al subir"); }
    finally { setBusy(false); }
  };

  const descargar = async (d) => {
    try {
      const r = await api.get(`/api/portal/equipos/${id}/documentos/${d.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("No se pudo abrir el documento"); }
  };

  const eliminarDoc = async (d) => {
    if (!window.confirm("¿Eliminar este documento?")) return;
    try { await api.delete(`/api/portal/equipos/${id}/documentos/${d.id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const solicitar = async () => {
    setBusy(true);
    try {
      const r = await api.post("/api/portal/solicitudes",
        { equipo_id: id, tipo: solTipo, comentario: solComentario });
      if (r.data.notificado_whatsapp) {
        toast.success(`Solicitud ${r.data.folio} enviada — te contactaremos a la brevedad`);
      } else {
        toast.success(`Solicitud ${r.data.folio} registrada`);
        window.open(r.data.wa_link, "_blank"); // respaldo: abre WhatsApp con el mensaje listo
      }
      setSolTipo(null); setSolComentario("");
    } catch (e) { toast.error(e.response?.data?.detail || "Error al solicitar"); }
    finally { setBusy(false); }
  };

  if (!eq) return <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#0FB4B7]" /></div>;

  const docs = eq.documentos || [];

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <HardDrive className="h-5 w-5 text-[#0FB4B7] flex-shrink-0" />
            <span className="text-sm font-semibold text-[#384091] truncate">
              {eq.tipo_equipo}{eq.marca ? ` · ${eq.marca}` : ""}{eq.modelo ? ` ${eq.modelo}` : ""}
            </span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/cliente/dashboard")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {/* Ficha */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1.5 text-sm">
              {[["N° serie", eq.numero_serie], ["Box", eq.numero_box], ["Código", eq.codigo_interno],
                ["Adquirido", eq.fecha_adquisicion], ["Proveedor", eq.proveedor]].map(([l, v]) => (
                <p key={l}><span className="text-gray-400">{l}:</span> {v || "—"}</p>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Solicitar mantención */}
        <div className="grid grid-cols-2 gap-3">
          <Button className="h-14 bg-[#0FB4B7] hover:bg-[#0D999C] text-white text-sm font-semibold"
            onClick={() => setSolTipo("preventiva")}>
            <ClipboardCheck className="h-5 w-5 mr-2" /> Solicitar preventiva
          </Button>
          <Button className="h-14 bg-[#384091] hover:bg-[#2D3375] text-white text-sm font-semibold"
            onClick={() => setSolTipo("correctiva")}>
            <Wrench className="h-5 w-5 mr-2" /> Reportar falla
          </Button>
        </div>

        {solTipo && (
          <Card className="border-0 shadow-md ring-2 ring-[#0FB4B7]/30">
            <CardContent className="p-5 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-800">
                  {solTipo === "preventiva" ? "Solicitar mantención preventiva" : "Reportar falla (correctiva)"}
                </h3>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setSolTipo(null)}><X className="h-4 w-4" /></Button>
              </div>
              <Textarea rows={3} value={solComentario} onChange={(e) => setSolComentario(e.target.value)}
                placeholder={solTipo === "correctiva" ? "Describe la falla: ¿qué pasa, desde cuándo, en qué box?" : "¿Alguna preferencia de fecha u horario?"}
                className="bg-white" />
              <Button className="w-full h-12 bg-[#25D366] hover:bg-[#1fb757] text-white font-semibold"
                onClick={solicitar} disabled={busy}>
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Enviar solicitud ahora"}
              </Button>
              <p className="text-[11px] text-gray-400 text-center">
                Tu solicitud llega de inmediato al equipo técnico de Odontofix.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Documentos */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-800 flex items-center gap-2">
                <FileText className="h-4 w-4 text-[#384091]" /> Repositorio documental
              </h2>
              <input ref={fileRef} type="file" accept=".pdf,.docx,image/*" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; pickFile(f); }} />
              <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()}>
                <Upload className="h-3.5 w-3.5 mr-1" /> Subir documento
              </Button>
            </div>
            <p className="text-[11px] text-gray-400">
              PDF, imagen o Word (máx. 10MB). Los archivos se comprimen y Word se convierte a PDF automáticamente.
            </p>

            {upl && (
              <div className="rounded-xl border-2 border-dashed border-[#0FB4B7]/40 bg-[#F0FDFA] p-4 space-y-3">
                <p className="text-sm font-medium text-gray-700 truncate">📄 {upl.file.name}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label className="text-xs">¿Qué tipo de documento es? *</Label>
                    <Select value={upl.tipo_documento} onValueChange={(v) => setUpl((p) => ({ ...p, tipo_documento: v }))}>
                      <SelectTrigger className="h-10 bg-white"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {Object.entries(TIPO_DOC_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select></div>
                  <div><Label className="text-xs">Fecha del documento</Label>
                    <Input type="date" value={upl.fecha_documento}
                      onChange={(e) => setUpl((p) => ({ ...p, fecha_documento: e.target.value }))}
                      className="h-10 bg-white no-uppercase" /></div>
                  <div className="sm:col-span-2"><Label className="text-xs">Descripción breve</Label>
                    <Input value={upl.descripcion} onChange={(e) => setUpl((p) => ({ ...p, descripcion: e.target.value }))}
                      placeholder="Ej: MP realizada por Odontofix, folio MP-2605-123" className="h-10 bg-white" /></div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="bg-[#0FB4B7] text-white" onClick={subir} disabled={busy}>
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar documento"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setUpl(null)}>Cancelar</Button>
                </div>
              </div>
            )}

            {docs.length === 0 ? (
              <p className="text-center text-gray-400 py-6 text-sm">
                Sin documentos aún. Sube la factura de compra, mantenciones o certificados.
              </p>
            ) : (
              <div className="space-y-2">
                {docs.map((d) => (
                  <div key={d.id} className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white p-3">
                    <div className="w-9 h-9 rounded-lg bg-[#384091]/10 flex items-center justify-center flex-shrink-0">
                      <FileText className="h-4 w-4 text-[#384091]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">
                        {TIPO_DOC_LABEL[d.tipo_documento] || d.tipo_documento}
                        {d.fecha_documento ? ` · ${d.fecha_documento}` : ""}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {d.descripcion || d.nombre_original} · {(d.size / 1024).toFixed(0)} KB
                      </p>
                    </div>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => descargar(d)} title="Ver / descargar">
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-400 hover:text-red-600" onClick={() => eliminarDoc(d)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
